import { useMemo } from 'react';
import { useCarriers } from './use-carriers-hook';
import {
  SPECIALTY_CARRIERS_CONFIG,
  SpecialtyCarrierConfig,
} from 'constants/specialtyCarriers';
import { APICarrierData } from 'types/api.types';

export interface AvailableCarrier extends SpecialtyCarrierConfig {
  isAvailable: boolean;
  dbCarrier: APICarrierData | undefined;
}

export interface SpecialtyCarrierFeatures {
  canUploadDeclaration: boolean;
  canUploadMVR: boolean;
  canGenerateDASH: boolean;
  hasAnyCarrier: boolean;
}

export const useSpecialtyCarriers = (lineOfBusiness?: 'Auto' | 'Home') => {
  const { carriers, carrierOptions, isLoadingCarriers } = useCarriers();

  const specialtyCarrierData = useMemo(() => {
    // Filter carriers by line of business if specified
    const filteredConfig = lineOfBusiness
      ? SPECIALTY_CARRIERS_CONFIG.filter(
          (c) => c.lineOfBusiness === lineOfBusiness,
        )
      : SPECIALTY_CARRIERS_CONFIG;

    // Map configured carriers to actual available carriers
    const availableCarriers: AvailableCarrier[] = filteredConfig.map(
      (config) => {
        const carrierFromDB = carriers.find(
          (c) => c.name.toLowerCase() === config.name.toLowerCase(),
        );

        return {
          ...config,
          isAvailable: !!carrierFromDB,
          dbCarrier: carrierFromDB,
        };
      },
    );

    // Filter for actually available carriers
    const availableCarriersList = availableCarriers.filter(
      (c) => c.isAvailable,
    );
    const missingCarriers = availableCarriers.filter((c) => !c.isAvailable);

    // Determine what features are available based on carriers
    const features: SpecialtyCarrierFeatures = {
      canUploadDeclaration: availableCarriersList.some(
        (c) => c.fileRequirements.declarationPages,
      ),
      canUploadMVR: availableCarriersList.some(
        (c) => c.fileRequirements.mvrReport,
      ),
      canGenerateDASH: availableCarriersList.some(
        (c) => c.fileRequirements.dashReport,
      ),
      hasAnyCarrier: availableCarriersList.length > 0,
    };

    return {
      availableCarriers: availableCarriersList,
      missingCarriers,
      features,
      isLoading: isLoadingCarriers,
      carriers,
      carrierOptions,
    };
  }, [carriers, carrierOptions, isLoadingCarriers, lineOfBusiness]);

  return specialtyCarrierData;
};
